import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core'
import { IQuestionComponent } from 'app/interfaces/IQuestionComponent'
import { CheckboxMatrix } from 'app/models/questions/CheckboxMatrix'
import { AbstractControl } from '@angular/forms'
import { OpenStatementErrorMatcher } from 'app/error-matchers/OpenStatementErrorMatcher'
import { SurveyViewerService } from 'app/modules/survey/survey-viewer/services/survey-viewer.service'
import {cloneDeep} from "lodash-es";
import {IValidationResponse, ValidateCommentsAIService} from "@store/validateCommentsAI.service";
import {BehaviorSubject, Subject, takeUntil} from "rxjs";
import {InputPersistenceService} from '../../../services/input-persistence.service';

@Component({
  selector: 'app-checkbox-matrix',
  templateUrl: './checkbox-matrix.component.html',
  styleUrls: ['./checkbox-matrix.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckboxMatrixComponent implements IQuestionComponent, OnInit, OnDestroy {

  @Input() question: CheckboxMatrix
  @Input() isSmallScreen: boolean
  @Input() activeLang: string

  questionFormControl: AbstractControl
  errorMatcher: OpenStatementErrorMatcher
  isValidAI: Record<string, Record<string, BehaviorSubject<IValidationResponse>>> = {};
  private ngUnsubscribe = new Subject<void>();

  constructor(
    public surveyViewerService: SurveyViewerService,
    private validateAI: ValidateCommentsAIService,
    private inputPersistence: InputPersistenceService
  ) { }

  ngOnInit(): void {
    this.errorMatcher = new OpenStatementErrorMatcher(this.question.isOptional)
    this.questionFormControl = this.surveyViewerService.activeSurvey$.value.form.get(this.question.key)
    this.question.rowKeys.forEach(rKey => {
      this.question.statementKeys.forEach(sKey => {
        const commentsControl = this.questionFormControl.get(rKey).get(this.question.commentsControlName).get(sKey);
        if (commentsControl) {
          this.surveyViewerService.changeFormUpdateStrategy(commentsControl, 'blur');
          // Restaurer la valeur sauvegardée si elle existe (priorité à la valeur en cours de saisie)
          const savedValue = this.inputPersistence.getValue(this.question.key, sKey, rKey);
          if (savedValue) {
            commentsControl.setValue(savedValue, {emitEvent: false});
          }
        }
      })
    });

    // Surveiller les changements du Survey pour restaurer les valeurs quand le formulaire se recharge
    this.surveyViewerService.activeSurvey$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(survey => {
      if (survey) {
        this.question.rowKeys.forEach(rKey => {
          this.question.statementKeys.forEach(sKey => {
            const newCommentsControl = survey.form
              .get(this.question.key)
              ?.get(rKey)
              ?.get(this.question.commentsControlName)
              ?.get(sKey);
            
            if (newCommentsControl) {
              const currentValue = newCommentsControl.value;
              const savedValue = this.inputPersistence.getValue(this.question.key, sKey, rKey);
              
              if (savedValue && (!currentValue || currentValue === '')) {
                setTimeout(() => {
                  newCommentsControl.setValue(savedValue, {emitEvent: false});
                }, 0);
              }
            }
          });
        });
      }
    });
  }

  protected readonly cloneDeep = cloneDeep;

  get isReversedStatements(): Array<any> {
    return this.question.reverseMobile ? cloneDeep(this.question.statementsOrder).reverse() : this.question.statementsOrder;
  }

  onBlur(value: string, sKey: string, rKey: string){
    this.questionFormControl.get(rKey).get(this.question.commentsControlName).get(sKey).setValue(value)
    // Note: On ne nettoie PAS ici car le formulaire peut se recharger juste après

    this.isValidAI[rKey] ??= {};
    this.isValidAI[rKey][sKey] ??= new BehaviorSubject<IValidationResponse>({isValid: true});
    if (!this.question.disableOpenStatementsRealtimeValidation) {
      this.validateAI.validateAnswerWithOpenAI(this.question.question[this.activeLang], value, this.activeLang)
        .then((response) => {
          this.isValidAI[rKey][sKey].next(response);
          this.updateQuestionAIStatus();
        });
    }
  }

  /**
   * Sauvegarde la valeur en temps réel (appelé à chaque frappe)
   */
  onInput(value: string, sKey: string, rKey: string){
    this.inputPersistence.saveValue(this.question.key, value, sKey, rKey);
  }

  private updateQuestionAIStatus() {
    const hasError = Object.values(this.isValidAI).some(row =>
      Object.values(row).some(subject => !subject.value.isValid)
    );
    this.validateAI.updateQuestionAIValidationStatus(this.question.key, !hasError);
  }

  ngOnDestroy(){
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
